import React, { useState, useEffect, useRef } from 'react';
import { PAGE, SettingsHeader } from './SettingsUI';
import { Save, Loader2, Upload, Trash2, Image as ImageIcon, Building2, Palette, FileSpreadsheet } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const EMPTY = {
  name: '', email: '', phone: '', address: '', abn: '',
  website: '', ndisRegistrationNumber: '', brandColor: '',
};

const DEFAULT_COLOR = '#4F46E5';

// One labelled text field.
function Field({ label, value, onChange, placeholder, type = 'text', hint }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{label}</label>
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:border-primary-400 focus:ring-1 focus:ring-primary-300 outline-none"
      />
      {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

export default function Branding() {
  const [form, setForm]       = useState(EMPTY);
  const [logo, setLogo]       = useState(null);   // data URL
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get('/organizations/branding')
      .then(r => {
        const d = r.data || {};
        setForm({
          name: d.name || '', email: d.email || '', phone: d.phone || '',
          address: d.address || '', abn: d.abn || '', website: d.website || '',
          ndisRegistrationNumber: d.ndisRegistrationNumber || '',
          brandColor: d.brandColor || '',
        });
        setLogo(d.logoDataUrl || null);
      })
      .catch(() => toast.error('Could not load branding'))
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const color = form.brandColor || DEFAULT_COLOR;

  const onPickLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Logo must be 2 MB or smaller'); return; }
    if (!/\.(png|jpe?g|webp|gif)$/i.test(file.name)) { toast.error('Use a PNG, JPG, WebP or GIF image'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('logo', file);
      const { data } = await api.post('/organizations/branding/logo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setLogo(data.logoDataUrl || null);
      toast.success('Logo updated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Logo upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removeLogo = async () => {
    try {
      await api.delete('/organizations/branding/logo');
      setLogo(null);
      toast.success('Logo removed');
    } catch {
      toast.error('Could not remove logo');
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/organizations/branding', form);
      setForm(f => ({ ...f, ...{
        name: data.name || '', email: data.email || '', phone: data.phone || '',
        address: data.address || '', abn: data.abn || '', website: data.website || '',
        ndisRegistrationNumber: data.ndisRegistrationNumber || '', brandColor: data.brandColor || '',
      }}));
      toast.success('Branding saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save branding');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-24 text-slate-400"><Loader2 className="animate-spin" /></div>;
  }

  return (
    <div className={PAGE}>
      <SettingsHeader
        title="Branding"
        description="Your logo, brand colour and business details appear at the top of every Excel & PDF export."
        actions={
          <button onClick={save} disabled={saving} className="btn-primary">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save branding
          </button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ── Left: logo + colour ── */}
        <div className="space-y-6">
          {/* Logo */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
            <div className="flex items-center gap-2 mb-3">
              <ImageIcon size={16} className="text-primary-600" />
              <h2 className="text-sm font-bold text-slate-900">Logo</h2>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-28 h-28 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
                {logo
                  ? <img src={logo} alt="Logo" className="max-w-full max-h-full object-contain" />
                  : <Building2 size={28} className="text-slate-300" />}
              </div>
              <div className="space-y-2">
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={onPickLogo} className="hidden" />
                <button
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
                >
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload logo
                </button>
                {logo && (
                  <button
                    onClick={removeLogo}
                    className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-100 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <Trash2 size={14} /> Remove
                  </button>
                )}
                <p className="text-[11px] text-slate-400">PNG, JPG or WebP · up to 2 MB.<br />PNG with transparency looks best.</p>
              </div>
            </div>
          </div>

          {/* Brand colour */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
            <div className="flex items-center gap-2 mb-3">
              <Palette size={16} className="text-primary-600" />
              <h2 className="text-sm font-bold text-slate-900">Brand colour</h2>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={color}
                onChange={(e) => set('brandColor', e.target.value.toUpperCase())}
                className="w-12 h-12 rounded-lg border border-slate-200 cursor-pointer bg-white p-1"
              />
              <input
                type="text"
                value={form.brandColor}
                onChange={(e) => set('brandColor', e.target.value)}
                placeholder={DEFAULT_COLOR}
                className="w-36 px-3 py-2 rounded-xl border border-slate-200 text-sm font-mono uppercase focus:border-primary-400 outline-none"
              />
              {form.brandColor && (
                <button onClick={() => set('brandColor', '')} className="text-xs text-slate-400 hover:text-slate-600 underline">
                  reset
                </button>
              )}
            </div>
            <p className="text-[11px] text-slate-400 mt-2">Used for the header bar/accents on your exports. Defaults to {DEFAULT_COLOR} if unset.</p>
          </div>

          {/* Live preview */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
            <div className="flex items-center gap-2 mb-3">
              <FileSpreadsheet size={16} className="text-primary-600" />
              <h2 className="text-sm font-bold text-slate-900">Export header preview</h2>
            </div>
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-3" style={{ background: color }}>
                {logo
                  ? <img src={logo} alt="" className="h-9 max-w-[120px] object-contain bg-white/90 rounded px-1.5 py-1" />
                  : <div className="h-9 px-2 flex items-center text-white/90 font-bold text-sm">{form.name || 'Your Organisation'}</div>}
                <div className="ml-auto text-right text-white">
                  <div className="text-sm font-bold leading-tight">{form.name || 'Your Organisation'}</div>
                  <div className="text-[10px] opacity-90 leading-tight">Generated {new Date().toLocaleDateString()}</div>
                </div>
              </div>
              <div className="px-4 py-2 text-[11px] text-slate-500 bg-slate-50 space-y-0.5">
                {form.abn && <div>ABN: {form.abn}</div>}
                {form.address && <div>{form.address}</div>}
                {(form.phone || form.email) && <div>{[form.phone, form.email].filter(Boolean).join('  ·  ')}</div>}
                {(form.website || form.ndisRegistrationNumber) && (
                  <div>{[form.website, form.ndisRegistrationNumber && `NDIS: ${form.ndisRegistrationNumber}`].filter(Boolean).join('  ·  ')}</div>
                )}
                {!form.abn && !form.address && !form.phone && !form.email && (
                  <div className="text-slate-400 italic">Fill in your details to see them here.</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Right: business details ── */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-4 self-start">
          <div className="flex items-center gap-2">
            <Building2 size={16} className="text-primary-600" />
            <h2 className="text-sm font-bold text-slate-900">Business details</h2>
          </div>
          <Field label="Organisation name" value={form.name} onChange={(v) => set('name', v)} placeholder="Acme Care Pty Ltd" />
          <Field label="ABN" value={form.abn} onChange={(v) => set('abn', v)} placeholder="12 345 678 901" />
          <Field label="NDIS registration number" value={form.ndisRegistrationNumber} onChange={(v) => set('ndisRegistrationNumber', v)} placeholder="4050000123" hint="Your Organisation ID: starts with 405, nine or ten digits." />
          <Field label="Address" value={form.address} onChange={(v) => set('address', v)} placeholder="123 Care St, Sydney NSW 2000" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone" value={form.phone} onChange={(v) => set('phone', v)} placeholder="(02) 1234 5678" />
            <Field label="Email" value={form.email} onChange={(v) => set('email', v)} type="email" placeholder="info@acmecare.com.au" />
          </div>
          <Field label="Website" value={form.website} onChange={(v) => set('website', v)} placeholder="https://acmecare.com.au" />
        </div>
      </div>
    </div>
  );
}
